// Admin perf FE-1: route chunk preload registry. index.tsx registers the
// lazy screen component for each menu path once at module load; the admin
// dropdown / main nav ask for a path to be warmed on open/hover. Pure and
// flag-free by design: the callers gate on enviroment.adminPerfRouteSuspense-
// Enabled, so with the flag off nothing here is ever invoked (registration
// is a Map insert with no side effect). Preloads are fire-and-forget: a
// failed chunk download is handled by lazyWithRetry at the real render.

type Preloadable = { preload: () => Promise<unknown> };

const registry = new Map<string, Preloadable>();

export function registerRoutePreloads(
  entries: Record<string, Preloadable>,
): void {
  for (const [path, component] of Object.entries(entries)) {
    registry.set(path, component);
  }
}

/** Warms the chunk registered for `path` (exact match). Unknown path = no-op. */
export function preloadRoute(path: string): void {
  const component = registry.get(path);
  if (!component) return;
  try {
    component.preload().catch(() => undefined);
  } catch {
    // never let a warming attempt reach the caller
  }
}

export function preloadRoutes(paths: readonly string[]): void {
  for (const path of paths) preloadRoute(path);
}

/** Defers `fn` to idle time (setTimeout(0) where requestIdleCallback is missing). */
export function whenIdle(fn: () => void): void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(fn, { timeout: 1000 });
  } else {
    window.setTimeout(fn, 0);
  }
}

/** Test/inspection helper: registered paths. */
export function registeredRoutePreloadPaths(): string[] {
  return Array.from(registry.keys());
}
